/**
 * Copy for the Add-to-Thrive scope dialog.
 *
 * A sibling of `preCompareCopy.ts`, not a shared module with it. The two
 * dialogs ask the same STRUCTURAL question and count different things: Compare
 * counts rows it has not looked at yet, Add counts rows it is about to write.
 * Sharing the rules (`scopeChoice.ts`) buys the reasoning; sharing the
 * sentences would force one of the two to say something slightly untrue.
 *
 * Every function takes the numbers it needs, so nothing here has to know about
 * React, filters, or where a count came from.
 */

export const PRE_ADD_TITLE = "Before adding";

/**
 * The question, always asked.
 *
 * `PRE_ADD_STATEMENT` and `preAddEverythingLabel` used to sit beside this for
 * the collapsed case — "both arms name the same rows, so state instead of
 * asking". Add cannot reach that case: its two counts come from different
 * rules, so equality never proves equal sets and the collapse is forced off.
 * Removed rather than left unreachable (/pr-review iter 2, Agent C N6).
 */
export const PRE_ADD_QUESTION = "What do you want to add?";

/**
 * The dialog's ONLY note about an empty outcome, and it covers the refusal.
 *
 * Its sibling — shown when the chosen arm dispatches rows and writes none —
 * was deleted on the product owner's call (2026-09-16): the screen is meant to
 * be clean, and that state is already described, more precisely and split by
 * cause, by `preAddScopeExcludedNote` and by `preAddNotCountedNote` on the
 * selection arm. (Their wording is not reproduced here. This comment quoted it
 * twice and drifted twice — a comment that quotes copy is a second copy of it,
 * and the second one is never updated.) The two rendering together and
 * saying overlapping things was itself a review finding (iter 3, Agent C I1).
 *
 * THIS one stays, and the distinction is the button: the runnable case has a
 * live control and two other sentences explaining it, while this case has an
 * `aria-disabled` one. A blocked button whose reason is not on screen is the
 * defect three reviewers independently raised in iteration 1, and deleting
 * this sentence would rebuild it.
 *
 * `add_resolvable === 0` means every row in scope is already in Thrive,
 * dismissed, or a W1 duplicate — the server answers that with a 409 or a 422,
 * so the dialog refuses rather than offering a run it knows will bounce. The
 * note above is for the runnable case and must not be shown here: it asserts
 * "a conflict nobody has decided", which is true only once already-added and
 * dismissed rows are excluded. One message covering both said something false
 * in four of its five reachable states (/pr-review iter 3, Agent C I1).
 */
export const PRE_ADD_NOTHING_TO_SEND =
  "There is nothing here to add. These contacts are already in Thrive, dismissed, or blocked until they are fixed in Thrive.";

export const PRE_ADD_CANCEL = "Cancel";
export const PRE_ADD_RUN = "Add";

function people(n: number): string {
  return `${n} ${n === 1 ? "contact" : "contacts"}`;
}

export function preAddSelectedLabel(n: number): string {
  return `Add the ${people(n)} you selected`;
}

/** The whole-scope arm when NO filter is narrowing the table. */
export function preAddAllLabel(n: number): string {
  return `Add all ${people(n)} in this list`;
}

/**
 * The whole-scope arm when a filter IS active.
 *
 * Names the filter explicitly, because "all" under a filter is the sentence a
 * recruiter is most likely to misread — and getting it wrong writes contacts
 * they were not looking at. This arm only became offerable once `select_all`
 * could carry the table's filters; before that the bulk action was disabled
 * here rather than send a request that meant something wider than the screen.
 */
export function preAddAllFilteredLabel(n: number): string {
  return `Add all ${people(n)} matching your filters`;
}

/**
 * The gap between what was ticked and what will be written.
 *
 * Stated SEPARATELY from the number, deliberately. The number now answers
 * "how many will be added"; this answers "why is that fewer than I picked".
 * Folding the second into the first is what the old panel did, and it is how
 * the screen came to promise writes the worker never made.
 *
 * IT NO LONGER NAMES A CAUSE, for the third time in this card and on the third
 * screen (/pr-review iter 6 synthesis, item 2). It said "an unresolved conflict
 * blocks them until you decide in Compare to Thrive". Its input is
 * `isExportable(row) && !willBeWritten(row, decision)`, and since 2026-09-17
 * `willBeWritten` also refuses a row nobody has compared — so this count holds
 * two populations with two different remedies, and the sentence described one.
 * A never-compared row has no conflict and nothing to decide; the recruiter
 * runs Compare. The dialog has no per-row data to tell them apart, so it points
 * at the surface that does.
 */
export function preAddNotCountedNote(notCounted: number): string | null {
  // `notCounted`, not `blocked`. The name outlived the sentence: "blocked"
  // implied a conflict someone has to clear, and half of these are rows nobody
  // has compared (/pr-review iter 9, Agent A).
  if (notCounted <= 0) return null;
  return `${people(notCounted)} ${notCounted === 1 ? "is" : "are"} not counted. Open the contacts table to see why.`;
}

/**
 * Why the "all" arm's number is smaller than the one on the CTA that opened it.
 *
 * The CTA counts ROWS ("Add Selected (all 1300)"); this dialog counts WRITES
 * (`add_writable`, 842). Both are right and a recruiter reading them side by
 * side has no way to know that, so the gap is named rather than left to be
 * discovered after the run. `preAddNotCountedNote` does the same job for the
 * selection arm, off a different number.
 *
 * THE GAP HAS TWO CAUSES AND THEY NEED DIFFERENT ACTIONS, which is why this
 * takes two numbers rather than one subtraction:
 *
 *   * `notSent` — rows a run never sends (already in Thrive, dismissed, W1
 *     duplicate). Nothing the recruiter does here changes them.
 *   * `sentNotWritten` — rows it sends and does not write: undecided W2/W3,
 *     AND (since 2026-09-17) rows nobody has compared. Only the first kind
 *     comes back flagged To review, which is why the sentence names neither
 *     and points at the list. Deciding the conflicts in Compare is
 *     exactly what turns them into writes.
 *
 * It also states the one thing the server's count cannot know: per-contact
 * decisions ride the request and are never persisted, so a conflict the
 * recruiter already chose to write is still missing from this number. The
 * count under-states in that case, which is the safe direction — but silently
 * under-stating is still a surprise, and this is where it stops being one.
 */
export function preAddScopeExcludedNote(
  notSent: number,
  sentNotWritten: number,
  total: number
): string | null {
  // TWO CAUSES, TWO NUMBERS. This took a single `excluded` — `allScopeTotal -
  // add_writable` — which folded "never leaves the building" together with
  // "goes, and comes back flagged". Those are different outcomes with
  // different next actions, and the dialog was already printing a second
  // sentence about one of them, so the two contradicted each other on any
  // screen that showed both (/pr-review iter 4, Agent C I1/I2). It was also
  // the last surface in this card still collapsing the dispatch/write split
  // the rest of the round established.
  const clauses: string[] = [];
  if (notSent > 0) {
    clauses.push(
      `${notSent} won't be sent at all: already in Thrive, dismissed, or blocked until fixed in Thrive`
    );
  }
  if (sentNotWritten > 0) {
    clauses.push(
      // SAME CORRECTION AS THE PANEL'S, ONE SCREEN EARLIER (/pr-review iter 6,
      // Agent A). This asserted a single cause, "an unresolved conflict", for
      // the whole `add_resolvable - add_writable` gap. Since 2026-09-17 that
      // gap also holds NEVER-COMPARED rows: `_add_writable_condition` gained a
      // `thrive_match_type IS NOT NULL` term and `_export_resolvable_condition`
      // did not. Those rows are not a conflict, nobody has to decide anything,
      // and they do NOT come back flagged To review. The dialog cannot tell the
      // two apart from these two counts, so it stops claiming to.
      `${sentNotWritten} will be sent but not written. Open the contacts table to see why`
    );
  }
  if (clauses.length === 0) return null;
  // PLURALISED, like `preAddNotCountedNote` above and unlike the first version of
  // this one, which rendered "1 of the 1 contacts" on any one-row fixture.
  return `Of the ${people(total)} here, ${clauses.join("; and ")}. Conflicts you have already chosen to write in Compare are not counted here; they will still go.`;
}

/**
 * The over-cap refusal, with the way out in the same sentence.
 *
 * The escape matters more than the limit: the other arm has no cap at all, so
 * a recruiter who reads only the first half learns they are stuck when they
 * are one radio away from not being.
 */
/**
 * `selected` is the number of IDS THE REQUEST WOULD CARRY, not the number the
 * dialog promises to add. Those differ whenever undecided conflicts are
 * selected, and the recruiter needs to hear about the one that is actually
 * too big — telling them "195 is over the limit of 200" would be nonsense.
 */
export function preAddTooManySelected(selected: number, max: number): string {
  return `That selection has ${selected} contacts to add, and a selection can add at most ${max} at a time. Add all of them instead, which has no limit. Or untick some rows and run it in batches.`;
}
